/**
 * The small set of things every screen is made of.
 *
 * Kept together rather than one file each: they are a vocabulary, and a
 * vocabulary you have to go looking for is one people reinvent inline.
 */
import type { ReactNode, MouseEvent } from 'react';
import { navigate, shouldIntercept } from '../lib/router.js';

export function Link(
  props: { href: string; className?: string; children: ReactNode }
): ReactNode {
  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!shouldIntercept(event.nativeEvent, event.currentTarget, window.location.origin)) return;
    event.preventDefault();
    navigate(props.href);
  };
  // A real <a href>, so the browser's own behaviours — cmd-click, middle
  // click, "copy link address", the status bar preview — keep working.
  return <a href={props.href} onClick={onClick} className={props.className}>{props.children}</a>;
}

export function Spinner({ label }: { label: string }): ReactNode {
  return (
    <div className="flex items-center gap-3 p-8 text-ink-400" role="status">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-ink-700 border-t-brand-400"
      />
      {label}
    </div>
  );
}

/**
 * An empty list and a failed request look identical if you only render rows.
 *
 * So they are different components with different words, and the failure one
 * always offers the way forward.
 */
export function Empty(
  { title, hint, action }:
  { title: string; hint?: string | undefined; action?: ReactNode }
): ReactNode {
  return (
    <div className="rounded-lg border border-dashed border-ink-800 p-10 text-center">
      <p className="text-ink-200">{title}</p>
      {hint ? <p className="mt-1 text-sm text-ink-400">{hint}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Failed(
  { message, detail, onRetry }:
  // Explicitly `| undefined`: exactOptionalPropertyTypes makes "absent" and
  // "present and undefined" different types, and a caller computing an
  // optional detail produces the second.
  { message: string; detail?: string | undefined; onRetry?: (() => void) | undefined }
): ReactNode {
  return (
    <div
      role="alert"
      className="rounded-lg border border-bad-400/40 bg-bad-400/5 p-6 text-sm"
    >
      <p className="font-medium text-bad-400">{message}</p>
      {detail ? <p className="mt-1 text-ink-400">{detail}</p> : null}
      {onRetry ? (
        <button type="button" onClick={onRetry} className="mt-4 text-brand-300 underline">
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function Button(
  props: {
    children: ReactNode;
    onClick?: () => void;
    type?: 'button' | 'submit';
    variant?: 'primary' | 'ghost';
    disabled?: boolean;
  }
): ReactNode {
  const base = 'rounded-md px-3 py-2 text-sm font-medium transition disabled:opacity-40';
  const variant = props.variant === 'ghost'
    ? 'border border-ink-800 text-ink-200 hover:bg-ink-900'
    : 'bg-brand-500 text-white hover:bg-brand-400';
  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled}
      className={`${base} ${variant}`}
    >
      {props.children}
    </button>
  );
}

/**
 * A supply badge.
 *
 * `null` and `0` are OPPOSITES and are worded as such (L8). "Unlimited" and
 * "Not printed" being one step apart in a dropdown is how someone picks the
 * wrong one; being different words in different colours is how they notice.
 */
export function Supply({ limit, issued }: { limit: number | null; issued: number }): ReactNode {
  if (limit === null) {
    return <Badge tone="neutral">Unlimited · {issued} minted</Badge>;
  }
  if (limit === 0) {
    return <Badge tone="warn">Not printed</Badge>;
  }
  const soldOut = issued >= limit;
  return (
    <Badge tone={soldOut ? 'bad' : 'good'}>
      {issued} of {limit}{soldOut ? ' · sold out' : ''}
    </Badge>
  );
}

export function Badge(
  { children, tone = 'neutral' }:
  { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'brand' }
): ReactNode {
  const tones = {
    neutral: 'border-ink-700 text-ink-400',
    good: 'border-good-400/40 text-good-400',
    warn: 'border-warn-400/40 text-warn-400',
    bad: 'border-bad-400/40 text-bad-400',
    brand: 'border-brand-400/40 text-brand-300',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tones[tone]}`}>
      {children}
    </span>
  );
}
